import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FiPlay, FiPause, FiMaximize, FiDownload } from 'react-icons/fi';
import './VideoSection.css';
import previewVideo from '../filmora_weddings_compressed_horizontal.mp4';
import compressedVideo from '../filmora_weddings_compressed.mp4';
import originalVideo from '../filmora_weddings.mp4';

const VideoSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  // Auto-play video when section comes into view
  React.useEffect(() => {
    if (isInView && videoRef.current) {
      const video = videoRef.current;
      
      // Only auto-play if not already playing
      if (video.paused) {
        video.play().catch(error => {
          // Auto-play was prevented, user will need to click play
          console.log('Auto-play prevented:', error);
          setIsPlaying(false);
        });
      }
    }
  }, [isInView]);

  const handlePlayPause = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    
    if (videoRef.current) {
      const video = videoRef.current;
      
      if (video.paused) {
        video.play().catch(error => {
          console.error('Play error:', error);
          setIsPlaying(false);
        });
      } else {
        video.pause();
      }
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      } else if (videoRef.current.msRequestFullscreen) {
        videoRef.current.msRequestFullscreen();
      }
    }
  };

  const handleDownload = (type) => {
    const link = document.createElement('a');
    link.href = type === 'compressed' ? compressedVideo : originalVideo;
    link.download = type === 'compressed' 
      ? 'pre_wedding_video_compressed.mp4' 
      : 'pre_wedding_video_original.mp4';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowDownloadMenu(false);
  };

  return (
    <section id="video" className="video-section" ref={sectionRef}>
      <div className="video-background">
        <div className="bg-shapes">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className={`shape shape-${i + 1}`}
              animate={{
                y: [-30, 30, -30],
                x: [-20, 20, -20],
                rotate: [0, 360]
              }}
              transition={{
                duration: 15 + i * 2,
                repeat: Infinity,
                delay: i * 1.5,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>
      </div>

      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="video-decoration"
            animate={{
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            🎬
          </motion.div>
          <h2>Our Pre-Wedding Story</h2>
          <p>A glimpse into our beautiful journey</p>
        </motion.div>

        <motion.div
          className="video-container"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="video-wrapper">
            <video
              ref={videoRef}
              src={previewVideo}
              className="video-player"
              onClick={(e) => {
                e.stopPropagation();
                handlePlayPause(e);
              }}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              controls={false}
            >
              Your browser does not support the video tag.
            </video>

            {/* Video Controls */}
            <div className="video-controls">
              <motion.button
                className="control-btn play-pause-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlayPause(e);
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                {isPlaying ? <FiPause /> : <FiPlay />}
              </motion.button>

              <motion.button
                className="control-btn fullscreen-btn"
                onClick={handleFullscreen}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiMaximize />
              </motion.button>

              <div className="download-wrapper">
                <motion.button
                  className="control-btn download-btn"
                  onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FiDownload />
                </motion.button>

                {showDownloadMenu && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '80px',
                      right: '0',
                      backgroundColor: '#ffffff',
                      borderRadius: '8px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                      padding: '8px',
                      minWidth: '200px',
                      zIndex: 999999
                    }}
                  >
                    <div
                      onClick={() => handleDownload('compressed')}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        backgroundColor: '#f5f5f5',
                        marginBottom: '4px',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#000'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                    >
                      📥 Compressed (9 MB)
                    </div>
                    <div
                      onClick={() => handleDownload('original')}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#000'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                    >
                      📥 Original (182 MB)
                    </div>
                    <div
                      onClick={() => setShowDownloadMenu(false)}
                      style={{
                        position: 'absolute',
                        top: '-12px',
                        right: '-12px',
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: '#ff4444',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        border: '2px solid white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                      }}
                    >
                      ×
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Play overlay when paused */}
            {!isPlaying && (
              <motion.div
                className="play-overlay"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlayPause(e);
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div
                  className="play-button-large"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FiPlay />
                </motion.div>
              </motion.div>
            )}
          </div>

          <motion.div
            className="video-description"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <p>
              Experience the magical moments leading up to our special day. 
              Watch our pre-wedding video and download it to keep these memories forever.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default VideoSection;

